import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "name-a-component"
    }}>{`Name a component`}</h1>
    <p>{`You give a name to your component in its `}<inlineCode parentName="p">{`composer.json`}</inlineCode>{` and `}<inlineCode parentName="p">{`module.xml`}</inlineCode>{` files. These files also contain other required configuration parameters, such as the module's schema version.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`Before you continue, make sure you have completed all of the following tasks:`}</p>
    <ul>
      <li parentName="ul">{`Create a `}<a parentName="li" {...{
          "href": "component-file-structure.md"
        }}>{`file structure`}</a>{`.`}</li>
      <li parentName="ul">{`Create the `}<a parentName="li" {...{
          "href": "required-configuration-files.md"
        }}>{`configuration files`}</a>{` you'll need.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "component-registration.md"
        }}>{`Register`}</a>{` your component.`}</li>
    </ul>
    <h2 {...{
      "id": "add-a-modulexml-file"
    }}>{`Add a `}<inlineCode parentName="h2">{`module.xml`}</inlineCode>{` file`}</h2>
    <p>{`Declare the component itself by adding a `}<inlineCode parentName="p">{`module.xml`}</inlineCode>{` file in the `}<inlineCode parentName="p">{`/etc`}</inlineCode>{` folder of your component.`}</p>
    <p>{`A component declares itself (that is, defines its name and existence) in the `}<inlineCode parentName="p">{`module.xml`}</inlineCode>{` file, located in the install directory at `}<inlineCode parentName="p">{`<ComponentName>/etc/`}</inlineCode>{`.`}</p>
    <p>{`The smallest working `}<inlineCode parentName="p">{`module.xml`}</inlineCode>{` file would look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:framework:Module/etc/module.xsd">
        <module name="Vendor_ComponentName"/>
</config>
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`name`}</inlineCode>{` parameter defines the name of your component. It is required for all components. If you do not use `}<a parentName="p" {...{
        "href": "../components/declarative-schema/index.md"
      }}>{`Declarative Schema`}</a>{` to help manage the installation and upgrade processes for your component, then you must also add the  `}<inlineCode parentName="p">{`setup_version`}</inlineCode>{` parameter to the `}<inlineCode parentName="p">{`module`}</inlineCode>{` line. Set the `}<inlineCode parentName="p">{`setup_version`}</inlineCode>{` value to your module's database schema version. Omit the `}<inlineCode parentName="p">{`setup_version`}</inlineCode>{` parameter if you use `}<a parentName="p" {...{
        "href": "../components/declarative-schema/index.md"
      }}>{`Declarative Schema`}</a>{`.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Avoid using "Ui" for your custom module name, because the `}<inlineCode parentName="p">{`%Vendor%_Ui`}</inlineCode>{` notation, required when specifying paths, might cause issues.`}</p>
    <h2 {...{
      "id": "add-a-composerjson-file"
    }}>{`Add a `}<inlineCode parentName="h2">{`composer.json`}</inlineCode>{` file`}</h2>
    <p><inlineCode parentName="p">{`composer.json`}</inlineCode>{` provides a component name and also specifies component dependencies.`}</p>
    <p>{`Refer to `}<a parentName="p" {...{
        "href": "../versioning/dependencies.md"
      }}>{`Module version dependencies`}</a>{` to determine versioning requirements.`}</p>
    <h3 {...{
      "id": "example"
    }}>{`Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "name": "your-name/module-Acme",
    "description": "Test component",
    "require": {
        "php": "~7.3.0||~7.4.0",
        "magento/module-store": "102.1",
        "magento/module-catalog": "102.1",
        "magento/module-catalog-inventory": "102.1",
        "magento/module-ui": "self.version",
        "magento/magento-composer-installer": "*"
    },
    "suggest": {
      "magento/module-webapi": "102.1"
    },
    "type": "magento2-module",
    "version": "102.1",
    "license": [
        "OSL-3.0",
        "AFL-3.0"
    ],
    "autoload": {
        "files": [ "registration.php" ],
        "psr-4": {
            "Magento\\\\CatalogImportExport\\\\": ""
        }
    }
}
`}</code></pre>
    <p>{`In this example:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode>{` is the name of your component.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`description`}</inlineCode>{` is a concise explanation of your component's purpose.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`require`}</inlineCode>{` lists any components your component depends on.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`suggest`}</inlineCode>{` lists soft dependencies. The component can operate without them, but if the components are active, this component might impact their functionality. `}<inlineCode parentName="li">{`Suggest`}</inlineCode>{` does not affect component load order.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`type`}</inlineCode>{` determines what the component type. Choose from `}<em parentName="li">{`magento2-theme`}</em>{`, `}<em parentName="li">{`magento2-language`}</em>{`, or `}<em parentName="li">{`magento2-module`}</em>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`version`}</inlineCode>{` lists the version of the component.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`license`}</inlineCode>{` lists applicable licenses that apply to your component.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`autoload`}</inlineCode>{` instructs Composer to load the specified files.`}</li>
    </ul>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Adobe Commerce and Magento Open Source do not currently support the `}<a parentName="p" {...{
        "href": "https://getcomposer.org/doc/05-repositories.md#path"
      }}><inlineCode parentName="a">{`path`}</inlineCode></a>{` repository.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      